/** @jsxRuntime classic */
/** @jsx jsx */
import React from 'react'
import { jsx, Box, Text, Label, Input, Select } from 'theme-ui'
import styles from './styles'
import { formatCamelCase } from '/utils/formatCamelCase'

const FormSectionUserInfo = ({
  cmsContent,
  register,
  errors,
  cmsErrorText,
  session,
}) => {
  const {
    guideText,
    labelName,
    placeholderName,
    labelPhoneNumber,
    placeholderPhoneNumber,
    phoneHelperText,
    gender,
    regionsOptions,
    labelDateOfBirth,
    placeholderDateOfBirth,
    contactMethod,
    optionalQuestions,
  } = cmsContent || {}

  const inputNameComponent = React.useMemo(
    () => (
      <React.Fragment>
        <Label htmlFor="name">{labelName}</Label>
        <Input
          type="text"
          name="name"
          placeholder={placeholderName}
          defaultValue={session ? session?.userDb?.name : ''}
          sx={errors.name && { border: '1px solid #E70041!important' }}
          {...register('name', {
            required: cmsErrorText,
          })}
        />
      </React.Fragment>
    ),
    [session, cmsContent],
  )

  const inputPhoneComponent = React.useMemo(
    () => (
      <React.Fragment>
        <Label htmlFor="phone_number">{labelPhoneNumber}</Label>
        <Input
          name="phone_number"
          placeholder={placeholderPhoneNumber}
          sx={errors.phone_number && { border: '1px solid #E70041!important' }}
          {...register('phone_number')}
        />
      </React.Fragment>
    ),
    [cmsContent],
  )

  const findErrorMessage = (value) => {
    const camelCaseValue = formatCamelCase(value)
    return camelCaseValue in errors ? true : false
  }

  return (
    <React.Fragment>
      <Box sx={styles.wrapper}>
        {guideText && (
          <Text sx={styles.guideText} as="p">
            {guideText}
          </Text>
        )}

        {/* Child components onChange re renders father, this makes inputs lose focus on typing */}
        {inputNameComponent}
        {errors.name && <Text sx={styles.formTextError}>{cmsErrorText}</Text>}

        {inputPhoneComponent}
        <Box sx={styles.bottomContent}>
          {errors.phone_number && (
            <Text sx={styles.formTextError}>{cmsErrorText}</Text>
          )}
          {phoneHelperText && (
            <Text sx={styles.helperText}>{phoneHelperText}</Text>
          )}
        </Box>

        <Label htmlFor="gender">{gender.label}</Label>
        <Box sx={styles.customCheckboxButton}>
          {gender.options.map(({ text, value }) => (
            <label key={value}>
              <input
                name="gender"
                id={value}
                type="radio"
                value={value}
                {...register('gender')}
              />
              <Text>{text}</Text>
            </label>
          ))}
        </Box>
        {errors.gender && <Text sx={styles.formTextError}>{cmsErrorText}</Text>}

        <Label htmlFor="region">{regionsOptions.label}</Label>
        <Select
          name="region"
          required
          sx={errors.region && { border: '1px solid #E70041!important' }}
          {...register('region', { required: cmsErrorText })}
        >
          <option value="" disabled selected>
            {regionsOptions.placeHolder}
          </option>
          {regionsOptions.options.map(({ text, value }) => (
            <option key={value} value={value.toLowerCase()}>
              {text}
            </option>
          ))}
        </Select>
        {errors.region && <Text sx={styles.formTextError}>{cmsErrorText}</Text>}

        <Label htmlFor="birthday">{labelDateOfBirth}</Label>
        <Input
          name="birthday"
          placeholder={placeholderDateOfBirth}
          defaultValue={session ? session?.userDb?.birthday : ''}
          sx={
            errors.birthday && {
              border: '1px solid #E70041!important',
            }
          }
          {...register('birthday', { required: cmsErrorText })}
        />
        {errors.birthday && (
          <Text sx={styles.formTextError}>{cmsErrorText}</Text>
        )}

        {optionalQuestions &&
          optionalQuestions.map(
            ({ label, labelEnglishValue, options, placeHolder }, index) => (
              <React.Fragment key={index}>
                <Label htmlFor={formatCamelCase(labelEnglishValue)}>
                  {label}
                </Label>
                <Select
                  name={formatCamelCase(labelEnglishValue)}
                  required
                  sx={
                    findErrorMessage(labelEnglishValue) && {
                      border: '1px solid #E70041!important',
                    }
                  }
                  {...register(formatCamelCase(labelEnglishValue), {
                    required: cmsErrorText,
                  })}
                >
                  <option value="" disabled selected>
                    {placeHolder}
                  </option>
                  {options.map(({ text, value }) => (
                    <option key={value} value={value}>
                      {text}
                    </option>
                  ))}
                </Select>
                {findErrorMessage(labelEnglishValue) && (
                  <Text sx={styles.formTextError}>{cmsErrorText}</Text>
                )}
              </React.Fragment>
            ),
          )}

        {contactMethod && (
          <React.Fragment>
            <Label htmlFor="contact">{contactMethod.label}</Label>
            <Box
              sx={{
                ...styles.customCheckboxButton,
                ...styles.customLargeCheckboxButton,
              }}
            >
              {contactMethod.options.map(({ text, value }) => (
                <React.Fragment key={value}>
                  <label key={value}>
                    <input
                      name="contact"
                      id={value}
                      type="radio"
                      value={value}
                      {...register('contact', {
                        required: cmsErrorText,
                      })}
                    />
                    <Text>{text}</Text>
                  </label>
                </React.Fragment>
              ))}
            </Box>
          </React.Fragment>
        )}
      </Box>
    </React.Fragment>
  )
}

export default FormSectionUserInfo
